import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Building2, Users, UserPlus, Map, Handshake, CreditCard, Settings, CalendarDays,
  ListTodo, Bell, Tags, Layers, Upload, Check, ChevronRight, X, Rocket, Sparkles,
} from "lucide-react";

const ICONS = {
  building: Building2, users: Users, "user-plus": UserPlus, map: Map, handshake: Handshake,
  "credit-card": CreditCard, settings: Settings, calendar: CalendarDays, list: ListTodo,
  bell: Bell, tags: Tags, layers: Layers, upload: Upload,
};

const HEADINGS = {
  admin: "Get your agency up and running",
  superadmin: "Set up your platform",
  agent: "Welcome aboard — let's get you started",
  manager: "Welcome aboard — let's get you started",
};

export function OnboardingChecklist({ onAddLead }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = () => api.get("/onboarding").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  if (data.dismissed || hidden) {
    return (
      <div className="flex justify-end">
        <button
          onClick={async () => { setHidden(false); try { await api.post("/onboarding/reset"); await load(); } catch { /* ignore */ } }}
          data-testid="onboarding-reopen"
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
          <Rocket className="h-3.5 w-3.5" /> Getting started guide
        </button>
      </div>
    );
  }

  const steps = data.steps || [];
  const done = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const allDone = done === total;

  const dismiss = async () => { setHidden(true); try { await api.post("/onboarding/dismiss"); } catch { /* ignore */ } };

  const act = (step) => {
    if (step.done) return;
    if (step.action === "add-lead") { onAddLead?.(); return; }
    navigate(step.action);
  };

  return (
    <Card className="overflow-hidden border-primary/20" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4 border-b border-border bg-primary/5 px-5 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            {allDone ? <Sparkles className="h-5 w-5" /> : <Rocket className="h-5 w-5" />}
          </span>
          <div>
            <h3 className="font-heading text-lg font-semibold">
              {allDone ? "You're all set!" : (HEADINGS[data.role] || "Getting started")}
            </h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {allDone ? "You've completed every step. You can dismiss this anytime."
                : `Finish these ${total} steps to make the most of IShareLeads.`}
            </p>
          </div>
        </div>
        <button onClick={dismiss} data-testid="onboarding-dismiss" title="Dismiss"
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-5 pt-4 sm:px-6">
        <div className="flex items-center justify-between text-xs font-medium">
          <span className="text-muted-foreground">Your progress</span>
          <span className="text-primary" data-testid="onboarding-progress">{done} of {total} done</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="divide-y divide-border px-2 py-2 sm:px-3">
        {steps.map((step) => {
          const Icon = ICONS[step.icon] || Check;
          return (
            <button key={step.key} onClick={() => act(step)} disabled={step.done}
              data-testid={`onboarding-step-${step.key}`}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors ${step.done ? "cursor-default opacity-70" : "hover:bg-accent"}`}>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors ${step.done ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-600" : "border-border bg-muted/40 text-muted-foreground group-hover:text-primary"}`}>
                {step.done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${step.done ? "line-through decoration-muted-foreground/50" : ""}`}>{step.title}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{step.desc}</span>
              </span>
              {!step.done && (
                <span className="ml-auto hidden shrink-0 items-center gap-1 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary sm:inline-flex">
                  {step.action_label} <ChevronRight className="h-3.5 w-3.5" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {allDone && (
        <div className="border-t border-border px-5 py-3 text-right sm:px-6">
          <button onClick={dismiss} data-testid="onboarding-finish"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
            <Check className="h-4 w-4" /> Done
          </button>
        </div>
      )}
    </Card>
  );
}
